'use client';

import type { TQrExperienceTheme } from '@shared/schemas';
import { Sun, Moon, Sparkles, SlidersHorizontal, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ThemeOption {
	id: TQrExperienceTheme;
	name: string;
	description: string;
	icon: typeof Sun;
	previewClass: string;
	badgeClass: string;
}

export const THEME_OPTIONS: ThemeOption[] = [
	{
		id: 'glass',
		name: 'Aurora Glass',
		description: 'Frosted glassmorphism with ambient color glow',
		icon: Sparkles,
		previewClass: 'bg-white/70 backdrop-blur-md border-white/40 text-slate-900 shadow-sm',
		badgeClass: 'bg-purple-100 text-purple-800',
	},
	{
		id: 'dark',
		name: 'Midnight Obsidian',
		description: 'Deep slate dark mode with high contrast glow',
		icon: Moon,
		previewClass: 'bg-slate-900 border-slate-800 text-white',
		badgeClass: 'bg-slate-800 text-slate-300',
	},
	{
		id: 'light',
		name: 'Clean Modern',
		description: 'Crisp white cards, subtle borders, vibrant accents',
		icon: Sun,
		previewClass: 'bg-white border-slate-200 text-slate-900',
		badgeClass: 'bg-slate-100 text-slate-700',
	},
	{
		id: 'minimal',
		name: 'Minimalist Crisp',
		description: 'Stark monochrome layout with hairline borders',
		icon: SlidersHorizontal,
		previewClass: 'bg-white border-black text-black',
		badgeClass: 'bg-black text-white',
	},
];

export const ACCENT_PRESETS = [
	{ name: 'Emerald', value: '#059669' },
	{ name: 'Royal Blue', value: '#1d4ed8' },
	{ name: 'Indigo', value: '#4f46e5' },
	{ name: 'Violet', value: '#7c3aed' },
	{ name: 'Rose', value: '#e11d48' },
	{ name: 'Amber', value: '#d97706' },
	{ name: 'Obsidian', value: '#0f172a' },
];

interface ThemeSelectorProps {
	selectedTheme?: TQrExperienceTheme;
	onThemeChange: (theme: TQrExperienceTheme) => void;
	accentColor?: string;
	onAccentColorChange?: (color: string) => void;
	headerColor?: string;
	onHeaderColorChange?: (color: string) => void;
}

export function ThemeSelector({
	selectedTheme = 'glass',
	onThemeChange,
	accentColor = '#059669',
	onAccentColorChange,
	headerColor,
	onHeaderColorChange,
}: ThemeSelectorProps) {
	return (
		<div className="space-y-6">
			{/* Theme Presets */}
			<div>
				<label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">
					Aesthetic Theme &amp; Mood
				</label>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					{THEME_OPTIONS.map((theme) => {
						const isSelected = selectedTheme === theme.id;
						const Icon = theme.icon;
						return (
							<button
								key={theme.id}
								type="button"
								onClick={() => onThemeChange(theme.id)}
								className={cn(
									'flex items-start gap-3 p-3.5 rounded-2xl border-2 text-left transition-all cursor-pointer',
									isSelected
										? 'border-emerald-600 bg-emerald-50/50 shadow-xs'
										: 'border-slate-200/90 hover:border-slate-300 bg-white',
								)}
							>
								<div
									className={cn(
										'size-10 rounded-xl flex items-center justify-center shrink-0 border transition-all',
										theme.previewClass,
									)}
								>
									<Icon className="size-5" />
								</div>
								<div className="min-w-0 flex-1">
									<div className="flex items-center justify-between">
										<span className="text-sm font-bold text-slate-900">{theme.name}</span>
										{isSelected && (
											<span className="flex size-5 items-center justify-center rounded-full bg-emerald-600 text-white">
												<Check className="size-3 stroke-[3]" />
											</span>
										)}
									</div>
									<p className="text-xs text-slate-500 mt-0.5 leading-snug line-clamp-2">
										{theme.description}
									</p>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			{/* Accent Color Palette */}
			{onAccentColorChange && (
				<div>
					<label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
						Brand Accent Color
					</label>
					<div className="flex flex-wrap items-center gap-2">
						{ACCENT_PRESETS.map((preset) => (
							<button
								key={preset.value}
								type="button"
								onClick={() => {
									onAccentColorChange(preset.value);
									if (onHeaderColorChange && !headerColor) {
										onHeaderColorChange(preset.value);
									}
								}}
								className={cn(
									'size-8 rounded-full border-2 transition-transform cursor-pointer relative flex items-center justify-center',
									accentColor?.toLowerCase() === preset.value.toLowerCase()
										? 'border-slate-900 scale-110 shadow-xs'
										: 'border-white hover:scale-105 shadow-2xs',
								)}
								style={{ backgroundColor: preset.value }}
								title={preset.name}
							>
								{accentColor?.toLowerCase() === preset.value.toLowerCase() && (
									<Check className="size-4 text-white stroke-[3]" />
								)}
							</button>
						))}
						<div className="flex items-center gap-1.5 ml-2">
							<input
								type="color"
								value={accentColor || '#059669'}
								onChange={(e) => onAccentColorChange(e.target.value)}
								className="size-8 rounded-lg cursor-pointer border border-slate-300 p-0.5 bg-white"
								title="Custom color"
							/>
							<span className="text-xs font-mono text-slate-500">{accentColor}</span>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
